import { pdf } from '@react-pdf/renderer';
import { t } from 'i18next';
import { useCallback } from 'react';

import QuotePdf from '~components/Project/QuotePdf/QuotePdf';
import type { AddOnAssignment } from '~hooks/useAddOns/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { Project } from '~hooks/useProjects/models';
import useProjectQuoteData from '~hooks/useProjects/useProjectQuoteData';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';
// Bundled brand asset because cdn.tread.io doesn't currently send CORS
// headers, which imageUrlToBase64 requires (Image.crossOrigin = 'anonymous'
// + canvas.toDataURL). Migrate to https://cdn.tread.io/tread-horizontal-black@2x.png
// once CORS is configured — tracked in TRE-14991.
import treadLogoPng from '~images/tread_logo_horizontal.png';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { imageUrlToBase64 } from '~utils/image/imageToBase64';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

export default function useQuotePdf() {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetchAddOnAssignments, fetchAllMaterialRates } = useProjectQuoteData();
  const { getCompanyLogoBase64 } = useCompany();
  const { currencyFormatter } = useCompanyCurrency();
  const { singular } = useDispatchNumberLabel();
  const { getAllProjectSiteRateSchedules } = useSiteRateSchedule();
  const fetchImageBase64Map = useCallback(
    async (imageUrls: string[]): Promise<Map<string, string>> => {
      const entries = await Promise.all(
        imageUrls.map(async (url): Promise<[string, string] | null> => {
          const base64 = await imageUrlToBase64(url);
          if (!base64) {
            console.warn(`Failed to convert image to base64 for quote PDF: ${url}`);
          }
          return base64 ? [url, base64] : null;
        }),
      );
      return new Map(entries.filter((e): e is [string, string] => e !== null));
    },
    [],
  );

  return useCallback(
    async (project: Project) => {
      try {
        const projectCompanyId = project.company?.id || companyId;
        const [
          companyLogoBase64,
          imageBase64Map,
          materialRates,
          addOnAssignments,
          siteRateSchedules,
        ] = await Promise.all([
          companyId ? getCompanyLogoBase64(companyId) : Promise.resolve(null),
          fetchImageBase64Map([treadLogoPng]),
          projectCompanyId
            ? fetchAllMaterialRates(projectCompanyId, project.id)
            : Promise.resolve([] as MaterialRate[]),
          projectCompanyId
            ? fetchAddOnAssignments(projectCompanyId, project.id)
            : Promise.resolve([] as AddOnAssignment[]),
          getAllProjectSiteRateSchedules(project.id),
        ]);

        const pdfDocument = (
          <QuotePdf
            project={project}
            company={userStore.userCompany}
            companyLogoBase64={companyLogoBase64 ?? undefined}
            currencyFormatter={currencyFormatter}
            materialRates={materialRates}
            addOnAssignments={addOnAssignments}
            treadLogoBase64={imageBase64Map.get(treadLogoPng)}
            siteRateSchedules={siteRateSchedules}
            dispatchNumberLabel={singular('project.quote_pdf.dispatch_number')}
          />
        );
        const blob = await pdf(pdfDocument).toBlob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        toasterStore.push(
          alert(t('project.quote_pdf.generation_failed'), AlertTypes.error),
        );
        console.error('Failed to generate quote PDF:', error);
      }
    },
    [
      companyId,
      currencyFormatter,
      fetchAddOnAssignments,
      fetchAllMaterialRates,
      fetchImageBase64Map,
      getAllProjectSiteRateSchedules,
      getCompanyLogoBase64,
      singular,
      toasterStore,
      userStore.userCompany,
    ],
  );
}
